import { useCallback, useEffect, useState } from 'react';
import { myCalendly, type CalendlyConnectionStatus, type CalendlyEventTypeOption } from '@/lib/api/calendlyClient';
import { toastError, toastSuccess } from '@/lib/toast';
import { LoadState } from "@/components/ui/LoadState"

const inputCls =
  'h-10 w-full rounded-lg border border-gray-300 px-3 text-[13px] text-gray-800 focus:border-brand-500 focus:outline-none focus:ring-2 focus:ring-brand-500/10 dark:border-gray-700 dark:bg-gray-900 dark:text-white';

// cccd-expert-meetings-confirm-redesign: conexión self-service del propio
// usuario con su cuenta de Calendly — nunca la configura un admin en nombre
// de otra persona, el Personal Access Token es un secreto personal.
// Extraído de la antigua MyCalendlyPage.tsx (gestion-staff-my-account) para
// montarse dentro del tab "Integraciones" de /account.
export default function CalendlyConnectionPanel() {
  const [status, setStatus] = useState<CalendlyConnectionStatus | null>(null);
  const [loading, setLoading] = useState(true);
  const [token, setToken] = useState('');
  const [connecting, setConnecting] = useState(false);
  const [eventTypes, setEventTypes] = useState<CalendlyEventTypeOption[] | null>(null);
  const [selecting, setSelecting] = useState(false);
  const [disconnecting, setDisconnecting] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      setStatus(await myCalendly.getStatus());
    } catch (err: any) {
      toastError(err?.response?.data?.error ?? 'Error al cargar el estado de la conexión.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { void load(); }, [load]);

  async function onConnect() {
    if (!token.trim()) return;
    setConnecting(true);
    try {
      const { eventTypes: types } = await myCalendly.connect(token.trim());
      setEventTypes(types);
      setToken('');
      if (types.length === 0) {
        toastError('Tu cuenta de Calendly no tiene tipos de evento activos para elegir.');
      }
    } catch (err: any) {
      toastError(err?.response?.data?.error ?? 'Token inválido o Calendly no respondió.');
    } finally {
      setConnecting(false);
    }
  }

  async function onSelectEventType(uri: string) {
    setSelecting(true);
    try {
      const result = await myCalendly.selectEventType(uri);
      toastSuccess(
        result.webhooksEnabled
          ? 'Conectado — el cupo se libera automáticamente si un equipo cancela.'
          : 'Conectado — si un equipo cancela una reunión, tendrá que liberar su cupo él mismo (tu plan de Calendly no soporta liberación automática).'
      );
      setEventTypes(null);
      await load();
    } catch (err: any) {
      toastError(err?.response?.data?.error ?? 'No se pudo guardar el tipo de evento.');
    } finally {
      setSelecting(false);
    }
  }

  async function onDisconnect() {
    setDisconnecting(true);
    try {
      await myCalendly.disconnect();
      toastSuccess('Calendly desconectado.');
      setEventTypes(null);
      await load();
    } catch (err: any) {
      toastError(err?.response?.data?.error ?? 'No se pudo desconectar.');
    } finally {
      setDisconnecting(false);
    }
  }

  if (loading) {
    return <LoadState />;
  }

  return (
    <div className="max-w-xl rounded-2xl border border-gray-200 bg-white p-6 dark:border-gray-800 dark:bg-white/[0.03]">
      {status?.connected && !eventTypes ? (
        <div className="space-y-4">
          <div className="flex items-center gap-2">
            <span className="rounded-full bg-success-50 px-2.5 py-1 text-xs text-success-600 dark:bg-success-500/10">Conectado</span>
            <span className="text-xs text-gray-500 dark:text-gray-400">
              {status.webhooksEnabled
                ? 'Liberación de cupo al cancelar: automática'
                : 'Liberación de cupo al cancelar: el equipo debe reportarlo él mismo'}
            </span>
          </div>
          {status.schedulingUrl && (
            <p className="break-all text-[13px] text-gray-500 dark:text-gray-400">{status.schedulingUrl}</p>
          )}
          <button
            type="button"
            onClick={onDisconnect}
            disabled={disconnecting}
            className="h-10 rounded-lg border border-error-200 px-4 text-[13px] font-medium text-error-600 hover:bg-error-50 disabled:opacity-60 dark:border-error-500/30 dark:hover:bg-error-500/10"
          >
            {disconnecting ? 'Desconectando…' : 'Desconectar'}
          </button>
        </div>
      ) : eventTypes ? (
        <div className="space-y-3">
          <p className="mb-2 text-sm font-semibold text-gray-600 dark:text-gray-400">Elige el tipo de evento para las reuniones</p>
          {eventTypes.map((et) => (
            <button
              key={et.uri}
              type="button"
              disabled={selecting}
              onClick={() => onSelectEventType(et.uri)}
              className="flex w-full items-center justify-between rounded-lg border border-gray-200 px-4 py-3 text-left text-[13px] hover:border-brand-500 hover:bg-brand-50/50 disabled:opacity-60 dark:border-gray-700 dark:hover:bg-brand-500/10"
            >
              <span>{et.name}</span>
              <span className="text-xs text-gray-500 dark:text-gray-400">Elegir</span>
            </button>
          ))}
        </div>
      ) : (
        <div className="space-y-3">
          <label className="mb-1.5 block text-[11px] text-gray-500 dark:text-gray-400">Personal Access Token de Calendly</label>
          <input
            type="password"
            value={token}
            onChange={(e) => setToken(e.target.value)}
            placeholder="Pégalo desde calendly.com/integrations/api_webhooks"
            className={inputCls}
          />
          <button
            type="button"
            onClick={onConnect}
            disabled={!token.trim() || connecting}
            className="h-10 whitespace-nowrap rounded-lg bg-brand-500 px-[18px] text-[13px] font-medium text-white hover:bg-brand-600 disabled:opacity-60"
          >
            {connecting ? 'Conectando…' : 'Conectar'}
          </button>
        </div>
      )}
    </div>
  );
}
